import { makeAutoObservable } from 'mobx'

const DESCRIPTION_MIN_LENGTH = 40
const DESCRIPTION_MAX_LENGTH = 1000
const VIDEO_MAX_SIZE_BYTES = 100 * 1024 * 1024
const VIDEO_ALLOWED_TYPES = ['video/mp4', 'video/quicktime']
const VIDEO_ALLOWED_EXTENSIONS = ['mp4', 'mov']
const VIDEO_TYPE_ERROR = {
  title: 'The file is not supported for upload',
  desc: 'Please upload only: mp4, mov',
}
const VIDEO_SIZE_ERROR = {
  desc: 'The video file is too large. It must not exceed 100 MB',
}

type IVideoError = { title?: string; desc: string }

export class UseCaseStore {
  private _description = ''
  private _video: File | null = null
  private _videoError: IVideoError | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get description() {
    return this._description
  }

  get video() {
    return this._video
  }

  get videoError() {
    return this._videoError
  }

  private get _isDescriptionValid() {
    const { length } = this._description
    return length >= DESCRIPTION_MIN_LENGTH && length <= DESCRIPTION_MAX_LENGTH
  }

  get descriptionMinLength() {
    return DESCRIPTION_MIN_LENGTH
  }

  get descriptionMaxLength() {
    return DESCRIPTION_MAX_LENGTH
  }

  get isNextDisabled() {
    return !this._isDescriptionValid
  }

  setDescription = (value: string) => {
    this._description = value
  }

  setVideo = (files: File[] | FileList) => {
    const [file] = Array.from(files)
    if (!file) return

    const error = this._getVideoError(file)
    if (error) {
      this._videoError = error
      return
    }

    this._videoError = null
    this._video = file
  }

  removeVideo = () => {
    this._video = null
    this._videoError = null
  }

  clearVideoError = () => {
    this._videoError = null
  }

  private _getVideoError = (file: File): IVideoError | null => {
    if (!this._isVideoTypeAllowed(file)) return VIDEO_TYPE_ERROR
    if (file.size > VIDEO_MAX_SIZE_BYTES) return VIDEO_SIZE_ERROR
    return null
  }

  private _isVideoTypeAllowed = (file: File) => {
    if (VIDEO_ALLOWED_TYPES.includes(file.type)) return true

    const extension = file.name.split('.').pop()?.toLowerCase()
    return extension ? VIDEO_ALLOWED_EXTENSIONS.includes(extension) : false
  }

  reset = () => {
    this._description = ''
    this._video = null
    this._videoError = null
  }
}
